import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import BreadCrumb from "Common/BreadCrumb";
import {
    X, Download, RefreshCw, MapPin,
    ArrowUpDown,
    ChevronDown,
    ChevronUp
} from "lucide-react";
import TableContainer from "Common/TableContainer";
import Modal from "Common/Components/Modal";
import { toast, ToastContainer } from "react-toastify";
import TableDropDownSmall from "pages/Components/TableDropDownSmall";
import { exportToExcel } from "Common/Components/Excel";
import LoadingCounter from "Common/Components/LoadingCounterProps";
import PaginationTest from "Common/Components/Paginationtest";
import axios from "axios";
import ImageLightbox from "Common/Components/ImageLightbox";
import MapViewer from "Common/Components/MapViewer";
import { getRoleId } from "Common/Components/getRoleId";
import { useNavigate } from "react-router-dom";
import GPSLocation from "Common/Components/GPSLocation";
import Swal from "sweetalert2";
import DriverTaskColumns from "./DriverDataColumn";
import { useAssistantFilter } from "Common/Assistant/lib/pageFilter";

// ========== Types ==========
interface OrderImage {
    created_at: string; created_at_jalali: string; id: number;
    category: string; url: string; file_name: string; file_size: number;
}

interface DriverTask {
    id: number;
    created_at: string;
    created_at_jalali: string;
    updated_at: string;
    updated_at_jalali: string;
    baker_name: string;
    baker_code: number;
    baker_phone: string;
    driver_discharge_count: number | null;
    driver_available_count: number | null;
    driver_location_lat: number | null;
    driver_location_lng: number | null;
    images: OrderImage[];
    // فیلدهای جدید
    discharge_date?: string | null;
    discharge_date_jalali?: string | null;
    driver_name?: string | null;
    driver_phone?: string | null;
    city?: string | null;
    province?: string | null;
    voucher_date?: string | null;
    voucher_date_jalali?: string | null;
    /** آدرس نانوا، خوانده‌شده از پروفایل خبازی توسط Backend */
    baker_address?: string | null;
}

interface TasksApiResponse {
    total: number; size: number; pages: number; items: DriverTask[];
}

// ========== CameraCapture ==========
const CameraCapture: React.FC<{
    maxCount: number;
    onChange: (files: File[]) => void;
    currentFiles: File[];
    buttonLabel: string;
    buttonClassName?: string;
}> = ({ maxCount, onChange, currentFiles, buttonLabel, buttonClassName = "" }) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const handleCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && currentFiles.length < maxCount) {
            onChange([...currentFiles, file]);
        }
        if (inputRef.current) inputRef.current.value = "";
    };

    const removeImage = (index: number) => {
        const updated = currentFiles.filter((_, i) => i !== index);
        onChange(updated);
    };

    return (
        <div>
            {/* <div className="flex items-center gap-2 mb-2">
                <Camera size={18} />
                <span className="text-sm font-medium text-gray-500">
                    حداکثر ۵ عکس <span className="font-bold text-blue-600 underline"></span>
                </span>
            </div>
            <input type="file" accept="image/*" capture="environment" style={{ display: "none" }} ref={inputRef} onChange={handleCapture} /> */}
            {/* <button
                type="button"
                disabled={currentFiles.length >= maxCount}
                onClick={() => inputRef.current?.click()}
                className={`px-4 py-2 rounded-lg text-sm font-medium text-white transition-all disabled:opacity-50 ${buttonClassName}`}
            >
                {buttonLabel}
            </button> */}
            {currentFiles.length > 0 && (
                <div className="mt-3 grid grid-cols-3 gap-2">
                    {currentFiles.map((file, idx) => (
                        <div key={idx} className="relative border rounded overflow-hidden">
                            <img src={URL.createObjectURL(file)} alt={`preview-${idx}`} className="w-full h-24 object-cover" />
                            <button onClick={() => removeImage(idx)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1">
                                <X size={12} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ========== GPSLocation (دریافت خودکار موقعیت، بدون دکمه، با امکان تلاش مجدد) ==========

const DriverTasks = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<"not_done" | "done">("not_done");
    const [tasks, setTasks] = useState<DriverTask[]>([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedNumber, setSelectedNumber] = useState<number>(10);

    // فیلتری که دستیار سامانه فرستاده (از گفتگو یا با صدا) را برمی‌دارد
    useAssistantFilter("driver-tasks", (filter) => {
        if (filter.status) setActiveTab(filter.status);
        if (filter.pageSize) setSelectedNumber(filter.pageSize);
        setSearchTerm(filter.search || "");
        setCurrentPage(1);
    });

    useEffect(() => {
        // پاک‌سازی اولیه
        document.body.classList.remove('overflow-hidden');
        document.body.style.overflow = '';

        // نگهبان: اگر کلاس overflow-hidden دوباره اضافه شد، بلافاصله حذف کن
        const observer = new MutationObserver(() => {
            if (document.body.classList.contains('overflow-hidden')) {
                document.body.classList.remove('overflow-hidden');
                document.body.style.overflow = '';
            }
        });

        observer.observe(document.body, {
            attributes: true,
            attributeFilter: ['class'],
        });

        return () => observer.disconnect();
    }, []);
    const [showSubmitModal, setShowSubmitModal] = useState(false);
    const [selectedTask, setSelectedTask] = useState<DriverTask | null>(null);

    const [driverLat, setDriverLat] = useState<number | null>(null);
    const [driverLng, setDriverLng] = useState<number | null>(null);
    const [dischargeCount, setDischargeCount] = useState<string>("");
    const [availableCount, setAvailableCount] = useState<string>("");
    const [preImages, setPreImages] = useState<File[]>([]);
    const [postImages, setPostImages] = useState<File[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [detailTask, setDetailTask] = useState<DriverTask | null>(null);
    const [showGalleryModal, setShowGalleryModal] = useState(false);
    const [galleryImages, setGalleryImages] = useState<OrderImage[]>([]);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const Token = localStorage.getItem("app_havir")
    const userRole = getRoleId(Token);

    const [sortBy, setSortBy] = useState<string | null>(null);
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');



    // 1. تابع fetchTasks برای بارگذاری صفحه‌بندی شده با قابلیت سورت
    const fetchTasks = useCallback(async () => {
        setLoading(true);
        try {
            const params: any = {
                page: currentPage,
                size: selectedNumber,
                status: activeTab,
                _t: Date.now(),
            };
            if (searchTerm.trim()) params.search = searchTerm.trim();

            // اضافه کردن پارامترهای سورت در صورت وجود
            if (sortBy) {
                params.sort_by = sortBy;
                params.sort_order = sortOrder;
            }

            const res = await axios.get<TasksApiResponse>(
                `https://rasad-api.havirard.ir/monitoring/tasks/driver`,
                {
                    params,
                    headers: { Authorization: `Bearer ${localStorage.getItem("app_havir")}` },
                }
            );

            // داده‌ها مستقیماً از سرور مرتب شده‌اند، نیازی به مرتب‌سازی دستی نیست
            setTasks(res.data.items || []);
            setTotal(res.data.total || 0);
            setTotalPages(res.data.pages || 0);
        } catch (err: any) {
            if (err.response?.status === 401) {
                localStorage.clear();
                navigate("/login");
            } else {
                toast.error("خطا در دریافت لیست وظایف");
            }
            setTasks([]);
        } finally {
            setLoading(false);
        }
    }, [currentPage, selectedNumber, searchTerm, activeTab, navigate, sortBy, sortOrder]); // اضافه شدن sortBy و sortOrder به وابستگی‌ها

    // 2. تابع fetchAllTasks برای دریافت همه داده‌ها (برای اکسل)
    // ===== داخل کامپوننت =====

    const fetchAllTasks = async (status: "not_done" | "done", search?: string) => {
        let allItems: DriverTask[] = [];
        let page = 1;
        const size = 100; // حداکثر مجاز
        let hasMore = true;

        while (hasMore) {
            try {
                const res = await axios.get<TasksApiResponse>(
                    `https://rasad-api.havirard.ir/monitoring/tasks/driver`,
                    {
                        params: {
                            page,
                            size,
                            status,
                            ...(search?.trim() && { search: search.trim() }),
                            _t: Date.now(),
                        },
                        headers: { Authorization: `Bearer ${localStorage.getItem("app_havir")}` },
                    }
                );
                const items = res.data.items || [];
                allItems = [...allItems, ...items];
                if (items.length < size) {
                    hasMore = false;
                } else {
                    page++;
                }
            } catch (error) {
                console.error("خطا در دریافت صفحه", page, error);
                hasMore = false;
                throw error; // خطا را به بالا منتقل می‌کند تا در handleExportExcel نمایش داده شود
            }
        }
        return allItems;
    };


    // 4. تابع handleExportExcel

    useEffect(() => {
        fetchTasks();
    }, [fetchTasks]);

    const handleSearchChange = (val: string) => {
        setSearchTerm(val);
        setCurrentPage(1);
    };

    const handlePageChange = (page: number) => setCurrentPage(page);
    const handleSizeChange = (val: number) => {
        setSelectedNumber(val);
        setCurrentPage(1);
    };
    const handleTabChange = (tab: "not_done" | "done") => {
        setActiveTab(tab);
        setCurrentPage(1);
    };

    const openSubmitModal = (task: DriverTask) => {
        setSelectedTask(task);
        setDriverLat(null);
        setDriverLng(null);
        setDischargeCount(task.driver_discharge_count != null ? String(task.driver_discharge_count) : "");
        setAvailableCount(task.driver_available_count != null ? String(task.driver_available_count) : "");
        setPreImages([]);
        setPostImages([]);
        setShowSubmitModal(true);
    };

    const closeSubmitModal = () => { setShowSubmitModal(false); setSelectedTask(null); };

    const openDetailModal = (task: DriverTask) => { setDetailTask(task); setShowDetailModal(true); };
    const openGallery = (images: OrderImage[]) => { setGalleryImages(images || []); setShowGalleryModal(true); };

    const uploadImages = async (orderId: number, category: "pre_unloading" | "post_unloading", files: File[]) => {
        if (files.length === 0) return;
        const formData = new FormData();
        files.forEach(file => formData.append("files", file));
        await axios.post(
            `https://rasad-api.havirard.ir/monitoring/orders/${orderId}/images/${category}`,
            formData,
            { headers: { Authorization: `Bearer ${localStorage.getItem("app_havir")}` } }
        );
    };

    const handleSubmitTask = async () => {
        if (!selectedTask) return;
        if (preImages.length > 5 || postImages.length > 5) {
            toast.error("حداکثر ۵ تصویر مجاز است");
            return;
        }

        const dischargeNum = Number(dischargeCount) || 0;
        const availableNum = Number(availableCount) || 0;

        setSubmitting(true);
        try {
            await axios.put(
                `https://rasad-api.havirard.ir/monitoring/tasks/driver/${selectedTask.id}/submission`,
                {
                    driver_location_lat: driverLat === null ? 0 : driverLat,
                    driver_location_lng: driverLng === null ? 0 : driverLng,
                    driver_discharge_count: dischargeNum,
                    driver_available_count: availableNum,
                },
                { headers: { Authorization: `Bearer ${localStorage.getItem("app_havir")}` } }
            );

            if (preImages.length > 0) {
                await uploadImages(selectedTask.id, "pre_unloading", preImages);
            }
            if (postImages.length > 0) {
                await uploadImages(selectedTask.id, "post_unloading", postImages);
            }

            toast.success("اطلاعات با موفقیت ثبت شد");
            closeSubmitModal();
            fetchTasks();
        } catch (err: any) {
            console.log(err);
            // مدیریت خطاهای مربوط به مهلت تخلیه راننده (403)
            if (err.response?.status === 403) {
                Swal.fire("خطا در ثبت", "امکان ثبت وجود ندارد. مهلت ثبت تخلیه بار به اتمام رسیده است.", "error")
                // const message = err.response?.data?.message || "";
                // // پیام دقیقی که بک‌اند برمی‌گرداند
                // if (message.includes("مهلت ثبت تخلیه بار به اتمام رسیده") ||
                //     message.includes("امکان ثبت وجود ندارد")) {
                // toast.error();
                // } else {
                //     toast.error(message || "خطا در ثبت اطلاعات");
                // }
            } else {
                // toast.error(err.response?.data?.message || "خطا در ثبت اطلاعات");
                Swal.fire("امکان ثبت وجود ندارد. مهلت ثبت تخلیه بار به اتمام رسیده است.")
            }
        } finally {
            setSubmitting(false);
        }
    };

    // ===== تابع ترجمه پیام‌های خطا =====
    const translateErrorMessage = (message: string): string => {
        if (!message) return "خطای ناشناخته";

        const errorMap: { [key: string]: string } = {
            // خطاهای مربوط به حذف راننده
            "Cannot delete driver submission while inspector submission exists":
                "امکان حذف تسک راننده وجود ندارد زیرا تسک بازرس هنوز موجود است. ابتدا تسک بازرس را حذف کنید.",
            "Driver submission not found": "تسک راننده یافت نشد.",
            "You don't have permission": "شما دسترسی لازم برای این عملیات را ندارید.",
            "Task not found": "تسک مورد نظر یافت نشد.",
            "Invalid request": "درخواست نامعتبر است.",
            "Internal server error": "خطای داخلی سرور. لطفاً بعداً تلاش کنید.",
            "Unauthorized": "احراز هویت ناموفق. لطفاً مجدداً وارد شوید.",
            "Forbidden": "شما دسترسی به این عملیات را ندارید.",
            "Task is already completed": "این تسک قبلاً تکمیل شده است و قابل حذف نیست.",
            "Cannot delete": "امکان حذف وجود ندارد.",
            "submission not found": "ثبت‌نام مورد نظر یافت نشد.",
        };

        // بررسی تطابق کامل یا جزئی
        for (const [en, fa] of Object.entries(errorMap)) {
            if (message.includes(en)) {
                return fa;
            }
        }

        // در صورت عدم تطابق، همان پیام اصلی برگردانده می‌شود (یا می‌توانید پیام پیش‌فرض فارسی دهید)
        return message;
    };

    const openDELETEModal = async (Data: DriverTask) => {

        // ۱. نمایش دیالوگ تأیید
        const result = await Swal.fire({
            title: 'آیا مطمئن هستید؟',
            text: 'این عملیات قابل بازگشت نیست!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'بله، تأیید می‌کنم',
            cancelButtonText: 'لغو'
        });

        if (result.isConfirmed) {
            try {
                // دریافت توکن از localStorage (به جای متغیر تعریف‌نشده Token)
                const token = localStorage.getItem("app_havir");

                const response = await axios.delete(
                    `https://rasad-api.havirard.ir/monitoring/tasks/driver/${Data.id}/submission`,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                        }
                    }
                );

                if (response.status === 200) {
                    await Swal.fire({
                        icon: 'success',
                        title: 'عملیات با موفقیت انجام شد',
                        text: 'تسک راننده مورد نظر حذف گردید.'
                    });
                    fetchTasks(); // بارگذاری مجدد لیست
                }

            } catch (error: any) {
                let errorMessage = 'خطایی رخ داده است. لطفاً مجدداً تلاش کنید.';

                if (axios.isAxiosError(error)) {
                    const status = error.response?.status;

                    // دریافت پیام خطا از پاسخ سرور (اولویت با detail، سپس message، سپس error)
                    const serverMessage =
                        error.response?.data?.detail ||
                        error.response?.data?.message ||
                        error.response?.data?.error ||
                        "";

                    // اگر پیام سرور وجود داشت، ترجمه کن
                    if (serverMessage) {
                        errorMessage = translateErrorMessage(serverMessage);
                    } else {
                        // اگر پیامی از سرور نیامد، بر اساس status code پیام بده
                        if (status === 404) {
                            errorMessage = 'تسک مورد نظر یافت نشد.';
                        } else if (status === 403) {
                            errorMessage = 'شما دسترسی لازم برای این عملیات را ندارید.';
                        } else if (status === 400) {
                            errorMessage = 'درخواست نامعتبر است.';
                        } else if (status === 409) {
                            errorMessage = 'تضاد در داده‌ها. لطفاً ابتدا وابستگی‌ها را حذف کنید.';
                        } else if (status === 500) {
                            errorMessage = 'خطای داخلی سرور. لطفاً بعداً تلاش کنید.';
                        } else if (status === 401) {
                            errorMessage = 'احراز هویت ناموفق. لطفاً مجدداً وارد شوید.';
                        }
                    }
                } else if (error instanceof Error) {
                    errorMessage = error.message;
                }

                // نمایش خطا با SweetAlert2
                await Swal.fire({
                    icon: 'error',
                    title: 'خطا',
                    text: errorMessage,
                    confirmButtonColor: '#d33'
                });
            }
        }
    };

    const renderSortableHeader = (label: string, field: string) => {
        const sortField = field === "discharge_date_jalali" ? "discharge_date" : "inspection_date";
        const isActive = sortBy === sortField;
        return (
            <div
                className="flex items-center gap-1 cursor-pointer select-none"
                onClick={() => handleSort(field)}
            >
                {label}
                {isActive ? (
                    sortOrder === "asc" ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                ) : (
                    <ArrowUpDown size={16} className="text-gray-400" />
                )}
            </div>
        );
    };

    const handleSort = useCallback((field: string) => {
        let sortField = field;
        if (field === 'discharge_date_jalali') sortField = 'discharge_date';
        else if (field === 'inspection_date_jalali') sortField = 'inspection_date';
        else return;

        if (sortBy === sortField) {
            setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortBy(sortField);
            setSortOrder('asc');
        }
        setCurrentPage(1);
    }, [sortBy]);



    // ===== تعریف ستون‌ها با استفاده از کامپوننت جداگانه =====
    const columns = useMemo(() => {
        return DriverTaskColumns({
            userRole: userRole ?? 0, // بهتر است ۰ به جای ۱۵
            openSubmitModal,
            openGallery,
            openDetailModal,
            openDELETEModal,
            renderSortableHeader, // اضافه شد
        });
    }, [
        userRole,
        openSubmitModal,
        openGallery,
        openDetailModal,
        openDELETEModal,
        renderSortableHeader,
    ]);
    // const columns = useMemo(() => {
    //     const baseColumns: any[] = [
    //         // 1. ستون تاریخ ثبت (شرطی: اگر تاریخ تخلیه وجود دارد نشان بده، وگرنه تاریخ ایجاد)
    //         {
    //             header: "تاریخ ثبت",
    //             accessorKey: "created_at_jalali",
    //             enableColumnFilter: false,
    //             cell: (cell: any) => {
    //                 const row = cell.row.original;
    //                 // اگر تاریخ تخلیه وجود دارد، آن را نشان بده، وگرنه تاریخ ایجاد
    //                 return row.discharge_date_jalali || "-";
    //             }
    //         },

    //         // 2. ستون عملیات (فقط برای نقش‌های غیر از مدیر استان و شهرستان)
    //         ...(userRole !== 5 && userRole !== 6 ? [{
    //             header: "عملیات",
    //             enableColumnFilter: false,
    //             cell: (cell: any) => {
    //                 const task = cell.row.original as DriverTask;
    //                 return (
    //                     <div className="flex gap-2">
    //                         <button
    //                             onClick={() => openSubmitModal(task)}
    //                             className="p-2 text-green-700 border border-green-300 rounded-lg bg-green-50 hover:bg-green-100 hover:shadow-md transition-all duration-200"
    //                             title="ثبت تخلیه"
    //                         >
    //                             ثبت راننده
    //                         </button>
    //                     </div>
    //                 );
    //             }
    //         }] : []),

    //         // 3. شهرستان
    //         {
    //             header: "شهرستان",
    //             accessorKey: "city",
    //             enableColumnFilter: false,
    //             cell: (cell: any) => cell.getValue() || "نامشخص"
    //         },

    //         // 4. نام نانوا
    //         {
    //             header: "نام نانوا",
    //             accessorKey: "baker_name",
    //             enableColumnFilter: false,
    //             cell: (cell: any) => cell.getValue() || "-"
    //         },

    //         // 5. شماره تماس نانوا
    //         {
    //             header: "شماره تماس نانوا",
    //             accessorKey: "baker_phone",
    //             enableColumnFilter: false,
    //             cell: (cell: any) => cell.getValue() || "-"
    //         },

    //         // 6. تعداد کیسه موجود
    //         {
    //             header: "تعداد کیسه موجود",
    //             accessorKey: "driver_available_count",
    //             enableColumnFilter: false,
    //             cell: (cell: any) => cell.getValue() !== null ? cell.getValue() : "ثبت نشده"
    //         },

    //         // 7. تعداد کیسه تخلیه شده
    //         {
    //             header: "تعداد کیسه تخلیه شده",
    //             accessorKey: "driver_discharge_count",
    //             enableColumnFilter: false,
    //             cell: (cell: any) => cell.getValue() !== null ? cell.getValue() : "ثبت نشده"
    //         },

    //         // 8. مستندات تصویر
    //         {
    //             header: "مستندات تصویر",
    //             accessorKey: "images",
    //             enableColumnFilter: false,
    //             cell: (cell: any) => {
    //                 const images = cell.getValue() as OrderImage[] | undefined;
    //                 return images && images.length > 0 ? (
    //                     <button onClick={() => openGallery(images)} className="flex items-center gap-1 text-blue-600 hover:underline">
    //                         <Image size={14} /> {images.length} عکس
    //                     </button>
    //                 ) : <span className="text-gray-400">بدون تصویر</span>;
    //             }
    //         },

    //         // 9. نام راننده (در انتها - طبق مستند نسخه ۱۸)
    //         // 9. نام راننده
    //         {
    //             header: "نام راننده",
    //             accessorKey: "driver_name",
    //             cell: (cell: any) => cell.getValue() || "ثبت نشده"
    //         },
    //         // 10. شماره همراه راننده
    //         {
    //             header: "شماره همراه راننده",
    //             accessorKey: "driver_phone",
    //             cell: (cell: any) => cell.getValue() || "ثبت نشده"
    //         },
    //         // 11. تاریخ حواله (اگر API فیلد جداگانه دارد)
    //         {
    //             header: "تاریخ حواله",
    //             accessorKey: "voucher_date_jalali", // یا created_at_jalali
    //             cell: (cell: any) => cell.getValue() || "ثبت نشده"
    //         },
    //         ...(Number(userRole) === 0 || Number(userRole) === 1 ? [{
    //             header: "حذف",
    //             enableColumnFilter: false,
    //             cell: (cell: any) => {
    //                 const task = cell.row.original as DriverTask;
    //                 return (
    //                     <div className="flex gap-2">
    //                         <button
    //                             onClick={() => openDELETEModal(task)}
    //                             className="p-2 text-red-700 border border-green-300 rounded-lg bg-green-50 hover:bg-green-100 hover:shadow-md transition-all duration-200"
    //                             title="حذف "
    //                         >
    //                             حذف
    //                         </button>
    //                     </div>
    //                 );
    //             }
    //         }] : []),
    //     ];

    //     return baseColumns;
    // }, [userRole, openDetailModal, openSubmitModal, openGallery]);

    const handleExportExcel = async () => {
        try {
            Swal.fire("در حال دریافت اطلاعات", "در حال دریافت تمام داده‌ها...", "info");
            const allTasks = await fetchAllTasks(activeTab, searchTerm || undefined);
            if (allTasks.length === 0) {
                toast.warning("داده‌ای برای صادرات وجود ندارد");
                return;
            }
            const exportData = allTasks.map((t: DriverTask) => {
                // فقط برای تب done تاریخ ثبت را از discharge_date_jalali بگیر
                let registerDate = "-";
                if (activeTab === "done") {
                    registerDate = t.discharge_date_jalali || "-";
                }
                // برای not_done همان "-" باقی می‌ماند (بدون استفاده از created_at_jalali)

                return {
                    "شناسه": t.id,
                    "کد نانوا": t.baker_code,
                    "تاریخ ثبت": registerDate,
                    "شهرستان": t.city || "نامشخص",
                    "نام نانوا": t.baker_name || "-",
                    "شماره تماس نانوا": t.baker_phone || "-",
                    "تعداد کیسه موجود": t.driver_available_count ?? "ثبت نشده",
                    "تعداد کیسه تخلیه شده": t.driver_discharge_count ?? "ثبت نشده",
                    "تعداد تصاویر": t.images?.length ?? 0,
                    "نام راننده": t.driver_name || "ثبت نشده",
                    "شماره همراه راننده": t.driver_phone || "ثبت نشده",
                    "تاریخ حواله": t.voucher_date_jalali || "ثبت نشده",
                };
            });
            exportToExcel(exportData, `DriverTasks-${activeTab}-full`);
            toast.success(`${exportData.length} رکورد با موفقیت صادر شد`);
            Swal.close();
        } catch (error) {
            toast.error("خطا در دریافت یا صدور داده‌ها");
            console.error(error);
            Swal.close();
        }
    };
    return (
        <React.Fragment>
            <BreadCrumb pageTitle="ماموریت‌های من" title="پایش آرد" />
            <ToastContainer position="top-center" closeButton={false} limit={3} rtl={true} />
            <div className="card">
                <div className="card-body">
                    <div className="flex gap-4 mb-4 border-b dark:border-gray-700">
                        <button
                            onClick={() => handleTabChange("not_done")}
                            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === "not_done" ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500"}`}
                        >
                            ثبت‌نشده‌ها
                        </button>
                        <button
                            onClick={() => handleTabChange("done")}
                            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === "done" ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500"}`}
                        >
                            ثبت‌شده‌ها
                        </button>
                    </div>

                    <div className="flex flex-col gap-4 mb-4 md:flex-row md:items-center md:justify-between">
                        <div className="flex items-center gap-2">
                            <RefreshCw
                                className={`size-5 text-blue-500 cursor-pointer ${loading ? "animate-spin" : ""}`}
                                onClick={fetchTasks}
                            />
                            <h6 className="text-lg font-semibold">
                                مجموع وظایف{" "}
                                <span className="px-2 py-0.5 text-sm bg-blue-100 text-blue-700 rounded-full">
                                    {total}
                                </span>
                            </h6>
                        </div>
                        <div className="flex flex-wrap gap-3 items-center">
                            <TableDropDownSmall onSelect={handleSizeChange} />
                            <button onClick={handleExportExcel} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-white border border-blue-200 rounded-xl hover:bg-blue-50">
                                <Download size={16} /> اکسل
                            </button>
                        </div>
                    </div>
                    <div className="relative mb-2">
                        <input
                            type="text"   // ← به جای "search"
                            placeholder="جستجو..."
                            value={searchTerm}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            enterKeyHint="search"
                            className="w-64 pr-10 pl-4 py-2 text-sm border rounded-xl focus:ring-2 focus:ring-blue-500 dark:bg-gray-800"
                        />
                    </div>
                    {loading ? (
                        <LoadingCounter count={0} text="در حال بارگذاری..." />
                    ) : tasks.length === 0 ? (
                        <LoadingCounter count={0} text="وظیفه‌ای یافت نشد" />
                    ) : (
                        <>
                            <TableContainer
                                isPagination={false}
                                columns={columns}
                                data={tasks}
                                isGlobalFilter={false}
                                customPageSize={selectedNumber}
                                divclassName="-mx-5 overflow-x-auto"
                                tableclassName="w-full whitespace-nowrap"
                                theadclassName="ltr:text-left rtl:text-right bg-slate-100 dark:bg-zink-600"
                                thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-b border-slate-200 dark:border-zink-500"
                                tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-500"
                                PaginationClassName="hidden"
                            />
                            <PaginationTest
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                            />
                        </>
                    )}
                </div>
            </div>

            {/* Detail Modal */}
            <Modal
                show={showDetailModal}
                onHide={() => setShowDetailModal(false)}
                modal-center="true"
                className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
                dialogClassName="w-screen md:w-[42rem] bg-white rounded-2xl shadow-2xl dark:bg-gray-800"
            >
                <Modal.Header className="flex items-center justify-between p-5 border-b dark:border-gray-700">
                    <Modal.Title className="text-lg font-bold">جزئیات سفارش #{detailTask?.id}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-5 max-h-[70vh] overflow-y-auto">
                    {detailTask && (
                        <div className="space-y-3 text-sm">
                            <div><strong>نانوا:</strong> {detailTask.baker_name}</div>
                            <div><strong>تلفن نانوا:</strong> {detailTask.baker_phone}</div>
                            <div><strong>آدرس نانوا:</strong> {detailTask.baker_address || "ثبت نشده"}</div>
                            <div><strong>تعدادکیسه های تخلیه شده:</strong> {detailTask.driver_discharge_count ?? "ثبت نشده"}</div>
                            <div><strong> تعداد کیسه های موجود :</strong> {detailTask.driver_available_count ?? "ثبت نشده"}</div>
                            <div>
                                <strong className="flex items-center gap-1 mb-2">
                                    <MapPin size={16} /> موقعیت راننده:
                                </strong>
                                {detailTask.driver_location_lat != null && detailTask.driver_location_lng != null ? (
                                    <div className="-mx-5">
                                        <MapViewer
                                            lat={detailTask.driver_location_lat}
                                            lng={detailTask.driver_location_lng}
                                        />
                                    </div>
                                ) : (
                                    <p className="text-gray-400">ثبت نشده</p>
                                )}
                            </div>
                            <div>
                                <strong>گالری تصاویر:</strong>{" "}
                                {detailTask.images?.length > 0 ? (
                                    <button onClick={() => openGallery(detailTask.images)} className="text-blue-600 underline">
                                        {detailTask.images.length} عکس
                                    </button>
                                ) : (
                                    "ندارد"
                                )}
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="flex justify-end p-5 border-t dark:border-gray-700">
                    <button
                        onClick={() => setShowDetailModal(false)}
                        className="px-4 py-2 text-sm bg-gray-100 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600"
                    >
                        بستن
                    </button>
                </Modal.Footer>
            </Modal>

            {/* Gallery Modal */}
            <Modal
                show={showGalleryModal}
                onHide={() => setShowGalleryModal(false)}
                modal-center="true"
                className="fixed inset-0 bg-black/90 backdrop-blur-md flex items-center justify-center z-drawer"
                dialogClassName="w-[95vw] md:w-[48rem] bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl text-white p-0 overflow-hidden"
            >
                <Modal.Header className="flex items-center justify-between p-5 border-b border-white/10">
                    <Modal.Title className="text-lg text-white font-bold">جهت بزرگ نهایی روی تصویر کلیک کنید</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-5 max-h-[70vh] overflow-y-auto custom-scrollbar">
                    {galleryImages.length === 0 ? (
                        <p className="text-center text-white/50 py-10">تصویری وجود ندارد</p>
                    ) : (
                        (() => {
                            const preImages = galleryImages.filter(img => img.category === "pre_unloading");
                            const postImages = galleryImages.filter(img => img.category === "post_unloading");
                            const otherImages = galleryImages.filter(img => img.category !== "pre_unloading" && img.category !== "post_unloading");

                            return (
                                <div className="space-y-6">
                                    {preImages.length > 0 && (
                                        <div>
                                            <h4 className="text-sm font-semibold text-white mb-3 border-b border-white/10 pb-1">
                                                📸 قبل از تخلیه ({preImages.length} عکس)
                                            </h4>
                                            {/* <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                                {preImages.map((img, idx) => {
                                                    const globalIdx = galleryImages.findIndex(i => i.id === img.id);
                                                    return (
                                                        <div
                                                            key={img.id}
                                                            className="group relative border border-white/10 rounded-xl overflow-hidden cursor-pointer hover:scale-[1.02] transition-transform duration-300 bg-white/5"
                                                            onClick={() => { setLightboxIndex(globalIdx); setLightboxOpen(true); }}
                                                        >
                                                            <img src={img.url} alt={img.file_name} className="w-full h-40 object-cover" />
                                                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-2">
                                                                <span className="text-xs text-white bg-white/20 backdrop-blur-sm px-2 py-0.5 rounded-full">{img.file_name}</span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div> */}
                                        </div>
                                    )}
                                    {postImages.length > 0 && (
                                        <div>
                                            <h4 className="text-sm font-semibold text-white mb-3 border-b border-white/10 pb-1">
                                                📸 بعد از تخلیه ({postImages.length} عکس)
                                            </h4>
                                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                                {postImages.map((img, idx) => {
                                                    const globalIdx = galleryImages.findIndex(i => i.id === img.id);
                                                    return (
                                                        <div
                                                            key={img.id}
                                                            className="group relative border border-white/10 rounded-xl overflow-hidden cursor-pointer hover:scale-[1.02] transition-transform duration-300 bg-white/5"
                                                            onClick={() => { setLightboxIndex(globalIdx); setLightboxOpen(true); }}
                                                        >
                                                            <img src={img.url} alt={img.file_name} className="w-full h-40 object-cover" />
                                                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-2">
                                                                <span className="text-xs text-white bg-white/20 backdrop-blur-sm px-2 py-0.5 rounded-full">{img.file_name}</span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                    {otherImages.length > 0 && (
                                        <div>
                                            <h4 className="text-sm font-semibold text-white mb-3 border-b border-white/10 pb-1">
                                                📸 سایر تصاویر ({otherImages.length} عکس)
                                            </h4>
                                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                                {otherImages.map((img, idx) => {
                                                    const globalIdx = galleryImages.findIndex(i => i.id === img.id);
                                                    return (
                                                        <div
                                                            key={img.id}
                                                            className="group relative border border-white/10 rounded-xl overflow-hidden cursor-pointer hover:scale-[1.02] transition-transform duration-300 bg-white/5"
                                                            onClick={() => { setLightboxIndex(globalIdx); setLightboxOpen(true); }}
                                                        >
                                                            <img src={img.url} alt={img.file_name} className="w-full h-40 object-cover" />
                                                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-2">
                                                                <span className="text-xs text-white bg-white/20 backdrop-blur-sm px-2 py-0.5 rounded-full">{img.category} | {img.file_name}</span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })()
                    )}
                </Modal.Body>
                <Modal.Footer className="flex justify-end p-4 border-t border-white/10">
                    <button
                        onClick={() => setShowGalleryModal(false)}
                        className="px-5 py-2 text-sm bg-white/10 backdrop-blur-md border border-white/20 rounded-xl hover:bg-white/20 transition-colors text-white"
                    >
                        بستن
                    </button>
                </Modal.Footer>
            </Modal>

            {/* Submit Modal */}
            <Modal show={showSubmitModal} onHide={closeSubmitModal} modal-center="true"
                className="fixed flex flex-col transition-all duration-300 ease-in-out left-2/4 z-drawer -translate-x-2/4 -translate-y-2/4"
                dialogClassName="w-screen md:w-[45rem] bg-white rounded-2xl shadow-2xl dark:bg-gray-800">
                <Modal.Header className="flex items-center justify-between p-5 border-b">
                    <Modal.Title className="text-lg font-bold">ثبت تخلیه {selectedTask?.id}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-5 max-h-[70vh] overflow-y-auto space-y-6">
                    <GPSLocation onLocation={(lat, lng) => { setDriverLat(lat); setDriverLng(lng); }} lat={driverLat} lng={driverLng} />
                    <div>
                        <label className="block mb-1 text-sm font-medium">تعداد کیسه موجود قبل از تخلیه</label>
                        <input
                            type="text"
                            inputMode="numeric"
                            value={availableCount}
                            onChange={e => setAvailableCount(e.target.value)}
                            placeholder="تعداد را وارد کنید"
                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700"
                        />
                    </div>
                    <div>
                        <label className="block mb-1 text-sm font-medium">تعداد کیسه تخلیه شده</label>
                        <input
                            type="text"
                            inputMode="numeric"
                            value={dischargeCount}
                            onChange={e => setDischargeCount(e.target.value)}
                            placeholder="تعداد را وارد کنید"
                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-700"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <CameraCapture
                            maxCount={5}
                            onChange={setPreImages}
                            currentFiles={preImages}
                            buttonLabel="گرفتن عکس قبل از تخلیه"
                            buttonClassName="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-lg"
                        />
                        <CameraCapture
                            maxCount={5}
                            onChange={setPostImages}
                            currentFiles={postImages}
                            buttonLabel="گرفتن عکس بعد از تخلیه"
                            buttonClassName="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 shadow-lg"
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer className="flex justify-end gap-3 p-5 border-t">
                    <button onClick={closeSubmitModal} className="px-4 py-2 text-sm bg-gray-100 rounded-lg">انصراف</button>
                    <button onClick={handleSubmitTask} disabled={submitting}
                        className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg disabled:opacity-50">
                        {submitting ? "در حال ثبت..." : "ثبت تخلیه"}
                    </button>
                </Modal.Footer>
            </Modal>

            {lightboxOpen && (
                <ImageLightbox
                    images={galleryImages}
                    initialIndex={lightboxIndex}
                    onClose={() => setLightboxOpen(false)}
                />
            )}
        </React.Fragment>
    );
};

export default DriverTasks;